/**
 * =============================================================================
 * ExperiencePricing - Full Pricing Management Page
 * =============================================================================
 *
 * Robust page for managing experience pricing with:
 * - Full CRUD operations
 * - Category filtering
 * - Search
 * - Bulk actions
 * - Export
 * - Audit-friendly display
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    FormControlLabel,
    Switch,
    Chip,
    CircularProgress,
    Alert,
    Tooltip,
    Grid,
    Divider,
    InputAdornment,
    Tabs,
    Tab,
    Card,
    CardContent,
    Snackbar
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import DownloadIcon from '@mui/icons-material/Download';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import GroupsIcon from '@mui/icons-material/Groups';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import axios from 'axios';
import {
    listPricingCosts,
    createPricingCost,
    updatePricingCost,
    deletePricingCost,
    pricingActionError,
} from '../services/pricingProxyService';

const API_BASE = "https://api.whook.ai";
const CREDIT_VALUE = 40;

// Categories configuration
const CATEGORIES = [
    { value: 'all', label: 'All Categories', icon: <FilterListIcon />, color: '#888' },
    { value: 'wellness', label: 'Wellness', icon: <SelfImprovementIcon />, color: '#12c099' },
    { value: 'fitness', label: 'Fitness', icon: <FitnessCenterIcon />, color: '#C63D57' },
    { value: 'sports', label: 'Sports', icon: <SportsSoccerIcon />, color: '#3d8bc6' },
    { value: 'mindfulness', label: 'Mindfulness', icon: <SelfImprovementIcon />, color: '#9c27b0' },
    { value: 'team-building', label: 'Team Building', icon: <GroupsIcon />, color: '#ff9800' },
    { value: 'other', label: 'Other', icon: <MoreHorizIcon />, color: '#666' }
];

const RISK_LEVELS = [
    { value: 'low', label: 'Low', insurance: 25, color: '#12c099' },
    { value: 'medium', label: 'Medium', insurance: 40, color: '#ff9800' },
    { value: 'high', label: 'High', insurance: 60, color: '#C63D57' }
];

// Empty form template
const EMPTY_FORM = {
    sportName: '',
    category: 'wellness',
    instructorHourlyRate: 150,
    durationHours: 1,
    equipmentPerPersonTotal: 0,
    equipmentFixedTotal: 0,
    logisticsCost: 50,
    insuranceCost: 25,
    riskLevel: 'low',
    offsiteVenueHourlyRate: 100,
    requiresVenue: false,
    baseGroupSize: 30,
    isActive: true,
    notes: ''
};

const ExperiencePricing = () => {
    // State
    const [costs, setCosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedCost, setSelectedCost] = useState(null);
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);

    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [showInactive, setShowInactive] = useState(true);
    const [selectedTab, setSelectedTab] = useState(0);

    // Fetch costs on mount
    useEffect(() => {
        fetchCosts();
    }, []);

    const fetchCosts = async () => {
        setLoading(true);
        setError(null);
        try {
            // Try admin endpoint first (mgmt-backend proxy; whook gates it behind the internal key)
            const data = await listPricingCosts();
            if (data.success) {
                setCosts(data.costs || []);
            }
        } catch (err) {
            console.warn('Admin endpoint failed, trying fallback...');
            try {
                // Fallback to regular endpoint
                const fallback = await axios.get(`${API_BASE}/v1/agent/pricing/sports`);
                if (fallback.data.success) {
                    // Transform the data to match expected format
                    const transformedCosts = fallback.data.sports.map(sport => ({
                        _id: sport.sportName,
                        sportName: sport.sportName,
                        category: sport.category || 'other',
                        instructorHourlyRate: 150,
                        durationHours: sport.durationHours || 1,
                        equipmentPerPersonTotal: 0,
                        equipmentFixedTotal: 0,
                        logisticsCost: 50,
                        insuranceCost: 25,
                        riskLevel: sport.riskLevel || 'low',
                        offsiteVenueHourlyRate: 100,
                        requiresVenue: sport.requiresVenue || false,
                        baseGroupSize: 30,
                        isActive: true,
                        // Include pricing data from API
                        onsite: sport.onsite,
                        offsite: sport.offsite
                    }));
                    setCosts(transformedCosts);
                }
            } catch (e) {
                setError('Failed to fetch pricing data');
            }
        } finally {
            setLoading(false);
        }
    };

    // Calculate pricing preview
    const calculatePreview = (data) => {
        const instructorCost = (data.instructorHourlyRate || 150) * (data.durationHours || 1);
        const equipmentCost = ((data.equipmentPerPersonTotal || 0) * (data.baseGroupSize || 30)) + (data.equipmentFixedTotal || 0);
        const deliveryCost = instructorCost + equipmentCost + (data.logisticsCost || 0) + (data.insuranceCost || 0);
        const venueCost = (data.offsiteVenueHourlyRate || 100) * (data.durationHours || 1);
        const deliveryCostOffsite = deliveryCost + venueCost;

        const margin = 0.75;
        const priceOnsite = deliveryCost / (1 - margin);
        const priceOffsite = deliveryCostOffsite / (1 - margin);
        const creditsOnsite = Math.ceil(priceOnsite / CREDIT_VALUE);
        const creditsOffsite = Math.ceil(priceOffsite / CREDIT_VALUE);

        return {
            instructorCost,
            equipmentCost,
            venueCost,
            deliveryCost: Math.round(deliveryCost),
            deliveryCostOffsite: Math.round(deliveryCostOffsite),
            priceOnsite: creditsOnsite * CREDIT_VALUE,
            priceOffsite: creditsOffsite * CREDIT_VALUE,
            creditsOnsite,
            creditsOffsite,
            marginOnsite: Math.round(((creditsOnsite * CREDIT_VALUE - deliveryCost) / (creditsOnsite * CREDIT_VALUE)) * 100),
            marginOffsite: Math.round(((creditsOffsite * CREDIT_VALUE - deliveryCostOffsite) / (creditsOffsite * CREDIT_VALUE)) * 100)
        };
    };

    // Filtered and searched costs
    const filteredCosts = useMemo(() => {
        return costs.filter(cost => {
            const matchesSearch = cost.sportName?.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesCategory = categoryFilter === 'all' || cost.category === categoryFilter;
            const matchesActive = showInactive || cost.isActive !== false;
            return matchesSearch && matchesCategory && matchesActive;
        });
    }, [costs, searchTerm, categoryFilter, showInactive]);

    // Stats
    const stats = useMemo(() => {
        const active = costs.filter(c => c.isActive !== false).length;
        const byCategory = CATEGORIES.slice(1).map(cat => ({
            ...cat,
            count: costs.filter(c => c.category === cat.value).length
        }));
        const avgCredits = costs.length > 0
            ? Math.round(costs.reduce((sum, c) => sum + (calculatePreview(c).creditsOnsite || 0), 0) / costs.length)
            : 0;

        return { total: costs.length, active, byCategory, avgCredits };
    }, [costs]);

    // Open dialog for add/edit
    const openDialog = (cost = null) => {
        if (cost) {
            setSelectedCost(cost);
            setFormData({
                sportName: cost.sportName || '',
                category: cost.category || 'wellness',
                instructorHourlyRate: cost.instructorHourlyRate || 150,
                durationHours: cost.durationHours || 1,
                equipmentPerPersonTotal: cost.equipmentPerPersonTotal || 0,
                equipmentFixedTotal: cost.equipmentFixedTotal || 0,
                logisticsCost: cost.logisticsCost || 50,
                insuranceCost: cost.insuranceCost || 25,
                riskLevel: cost.riskLevel || 'low',
                offsiteVenueHourlyRate: cost.offsiteVenueHourlyRate || 100,
                requiresVenue: cost.requiresVenue || false,
                baseGroupSize: cost.baseGroupSize || 30,
                isActive: cost.isActive !== false,
                notes: cost.notes || ''
            });
        } else {
            setSelectedCost(null);
            setFormData(EMPTY_FORM);
        }
        setDialogOpen(true);
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setSelectedCost(null);
        setFormData(EMPTY_FORM);
    };

    // Handle form changes
    const handleChange = (field, value) => {
        setFormData(prev => {
            const updated = { ...prev, [field]: value };

            // Auto-update insurance based on risk level
            if (field === 'riskLevel') {
                const risk = RISK_LEVELS.find(r => r.value === value);
                if (risk) updated.insuranceCost = risk.insurance;
            }

            return updated;
        });
    };

    // Save cost
    const handleSave = async () => {
        if (!formData.sportName.trim()) {
            setError('Experience name is required');
            return;
        }

        setSaving(true);
        setError(null);

        try {
            if (selectedCost?._id) {
                await updatePricingCost(selectedCost._id, formData);
                setSuccess(`Updated "${formData.sportName}"`);
            } else {
                await createPricingCost(formData);
                setSuccess(`Created "${formData.sportName}"`);
            }
            closeDialog();
            fetchCosts();
        } catch (err) {
            setError(pricingActionError(err, 'Failed to save'));
        } finally {
            setSaving(false);
        }
    };

    // Delete cost
    const handleDelete = async () => {
        if (!selectedCost) return;

        setSaving(true);
        try {
            await deletePricingCost(selectedCost._id);
            setSuccess(`Deleted "${selectedCost.sportName}"`);
            setDeleteDialogOpen(false);
            setSelectedCost(null);
            fetchCosts();
        } catch (err) {
            setError(pricingActionError(err, 'Failed to delete'));
        } finally {
            setSaving(false);
        }
    };

    // Export to CSV
    const exportCSV = () => {
        const headers = ['Experience', 'Category', 'Instructor Rate', 'Duration', 'Equipment/Person', 'Equipment Fixed', 'Logistics', 'Insurance', 'Risk', 'Venue Rate', 'Credits (On-Site)', 'Credits (Off-Site)', 'Active'];
        const rows = filteredCosts.map(cost => {
            const preview = calculatePreview(cost);
            return [
                cost.sportName,
                cost.category,
                cost.instructorHourlyRate,
                cost.durationHours,
                cost.equipmentPerPersonTotal,
                cost.equipmentFixedTotal,
                cost.logisticsCost,
                cost.insuranceCost,
                cost.riskLevel,
                cost.offsiteVenueHourlyRate,
                preview.creditsOnsite,
                preview.creditsOffsite,
                cost.isActive ? 'Yes' : 'No'
            ].join(',');
        });

        const csv = [headers.join(','), ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `experience-pricing-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
    };

    const getCategoryColor = (category) => {
        return CATEGORIES.find(c => c.value === category)?.color || '#666';
    };

    const preview = calculatePreview(formData);

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                    <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700 }}>
                        Experience Pricing
                    </Typography>
                    <Typography sx={{ color: '#888', mt: 0.5 }}>
                        Manage pricing for all wellness experiences
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={exportCSV}
                        sx={{ borderColor: '#444', color: '#888', '&:hover': { borderColor: '#666' } }}
                    >
                        Export
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => openDialog()}
                        sx={{ bgcolor: '#12c099', '&:hover': { bgcolor: '#0fa17d' } }}
                    >
                        Add Experience
                    </Button>
                </Box>
            </Box>

            {/* Stats Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: '#252525', border: '1px solid #333' }}>
                        <CardContent>
                            <Typography sx={{ color: '#888', fontSize: '0.75rem', mb: 1 }}>TOTAL EXPERIENCES</Typography>
                            <Typography variant="h3" sx={{ color: '#fff', fontWeight: 700 }}>{stats.total}</Typography>
                            <Typography sx={{ color: '#12c099', fontSize: '0.85rem', mt: 1 }}>
                                {stats.active} active
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: '#252525', border: '1px solid #333' }}>
                        <CardContent>
                            <Typography sx={{ color: '#888', fontSize: '0.75rem', mb: 1 }}>AVG CREDITS (ON-SITE)</Typography>
                            <Typography variant="h3" sx={{ color: '#12c099', fontWeight: 700 }}>{stats.avgCredits}</Typography>
                            <Typography sx={{ color: '#888', fontSize: '0.85rem', mt: 1 }}>
                                ${stats.avgCredits * CREDIT_VALUE} per session
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: '#252525', border: '1px solid #333' }}>
                        <CardContent>
                            <Typography sx={{ color: '#888', fontSize: '0.75rem', mb: 1 }}>TARGET MARGIN</Typography>
                            <Typography variant="h3" sx={{ color: '#C63D57', fontWeight: 700 }}>75%</Typography>
                            <Typography sx={{ color: '#888', fontSize: '0.85rem', mt: 1 }}>
                                60-75% range
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: '#252525', border: '1px solid #333' }}>
                        <CardContent>
                            <Typography sx={{ color: '#888', fontSize: '0.75rem', mb: 1 }}>CREDIT VALUE</Typography>
                            <Typography variant="h3" sx={{ color: '#fff', fontWeight: 700 }}>${CREDIT_VALUE}</Typography>
                            <Typography sx={{ color: '#888', fontSize: '0.85rem', mt: 1 }}>
                                per credit
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                    placeholder="Search experiences..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="small"
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#666' }} /></InputAdornment>
                    }}
                    sx={{
                        width: 250,
                        '& .MuiOutlinedInput-root': {
                            color: '#fff',
                            bgcolor: '#252525',
                            '& fieldset': { borderColor: '#333' }
                        }
                    }}
                />

                <Tabs
                    value={categoryFilter}
                    onChange={(e, v) => setCategoryFilter(v)}
                    sx={{
                        '& .MuiTab-root': { color: '#666', minWidth: 'auto', px: 2 },
                        '& .Mui-selected': { color: '#12c099' },
                        '& .MuiTabs-indicator': { bgcolor: '#12c099' }
                    }}
                >
                    {CATEGORIES.map(cat => (
                        <Tab
                            key={cat.value}
                            value={cat.value}
                            label={cat.value === 'all' ? 'All' : cat.label}
                            icon={cat.icon}
                            iconPosition="start"
                        />
                    ))}
                </Tabs>

                <Box sx={{ flexGrow: 1 }} />

                <FormControlLabel
                    control={
                        <Switch
                            checked={showInactive}
                            onChange={(e) => setShowInactive(e.target.checked)}
                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#12c099' } }}
                        />
                    }
                    label="Show inactive"
                    sx={{ color: '#888' }}
                />

                <Tooltip title="Refresh">
                    <IconButton onClick={fetchCosts} sx={{ color: '#888' }}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Error/Success Messages */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Table */}
            {loading ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <CircularProgress sx={{ color: '#12c099' }} />
                    <Typography sx={{ color: '#888', mt: 2 }}>Loading pricing data...</Typography>
                </Box>
            ) : (
                <TableContainer component={Paper} sx={{ bgcolor: '#252525', border: '1px solid #333' }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#1e1e1e' }}>
                                <TableCell sx={{ color: '#888', fontWeight: 600 }}>Experience</TableCell>
                                <TableCell sx={{ color: '#888', fontWeight: 600 }}>Category</TableCell>
                                <TableCell sx={{ color: '#888', fontWeight: 600 }} align="right">Delivery Cost</TableCell>
                                <TableCell sx={{ color: '#888', fontWeight: 600 }} align="center">On-Site</TableCell>
                                <TableCell sx={{ color: '#888', fontWeight: 600 }} align="center">Off-Site</TableCell>
                                <TableCell sx={{ color: '#888', fontWeight: 600 }} align="center">Duration</TableCell>
                                <TableCell sx={{ color: '#888', fontWeight: 600 }} align="center">Risk</TableCell>
                                <TableCell sx={{ color: '#888', fontWeight: 600 }} align="center">Status</TableCell>
                                <TableCell sx={{ color: '#888', fontWeight: 600 }} align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredCosts.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} sx={{ color: '#666', textAlign: 'center', py: 8 }}>
                                        {searchTerm || categoryFilter !== 'all'
                                            ? 'No experiences match your filters'
                                            : 'No experiences configured. Click "Add Experience" to get started.'
                                        }
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredCosts.map((cost) => {
                                    const calc = calculatePreview(cost);
                                    const riskConfig = RISK_LEVELS.find(r => r.value === cost.riskLevel);

                                    return (
                                        <TableRow
                                            key={cost._id || cost.sportName}
                                            hover
                                            sx={{
                                                opacity: cost.isActive === false ? 0.5 : 1,
                                                '&:hover': { bgcolor: '#2a2a2a' }
                                            }}
                                        >
                                            <TableCell>
                                                <Typography sx={{ color: '#fff', fontWeight: 500 }}>
                                                    {cost.sportName}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={cost.category}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: `${getCategoryColor(cost.category)}20`,
                                                        color: getCategoryColor(cost.category),
                                                        textTransform: 'capitalize',
                                                        fontWeight: 500
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography sx={{ color: '#888' }}>
                                                    ${calc.deliveryCost}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Box>
                                                    <Typography sx={{ color: '#12c099', fontWeight: 700, fontSize: '1.1rem' }}>
                                                        {calc.creditsOnsite} cr
                                                    </Typography>
                                                    <Typography sx={{ color: '#666', fontSize: '0.75rem' }}>
                                                        ${calc.priceOnsite}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Box>
                                                    <Typography sx={{ color: '#C63D57', fontWeight: 700, fontSize: '1.1rem' }}>
                                                        {calc.creditsOffsite} cr
                                                    </Typography>
                                                    <Typography sx={{ color: '#666', fontSize: '0.75rem' }}>
                                                        ${calc.priceOffsite}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography sx={{ color: '#888' }}>
                                                    {cost.durationHours}h
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={cost.riskLevel}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: `${riskConfig?.color}20`,
                                                        color: riskConfig?.color,
                                                        textTransform: 'capitalize',
                                                        minWidth: 70
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                {cost.isActive !== false ? (
                                                    <CheckCircleIcon sx={{ color: '#12c099', fontSize: 20 }} />
                                                ) : (
                                                    <CancelIcon sx={{ color: '#666', fontSize: 20 }} />
                                                )}
                                            </TableCell>
                                            <TableCell align="center">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => openDialog(cost)}
                                                    sx={{ color: '#888', '&:hover': { color: '#12c099' } }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => { setSelectedCost(cost); setDeleteDialogOpen(true); }}
                                                    sx={{ color: '#888', '&:hover': { color: '#C63D57' } }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Results count */}
            {!loading && (
                <Typography sx={{ color: '#666', mt: 2, fontSize: '0.85rem' }}>
                    Showing {filteredCosts.length} of {costs.length} experiences
                </Typography>
            )}

            {/* Add/Edit Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={closeDialog}
                maxWidth="lg"
                fullWidth
                PaperProps={{ sx: { bgcolor: '#1e1e1e', color: '#fff', maxHeight: '90vh' } }}
            >
                <DialogTitle sx={{ borderBottom: '1px solid #333', pb: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {selectedCost ? 'Edit Experience' : 'Add New Experience'}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Grid container spacing={4}>
                        {/* Left Column - Form Fields */}
                        <Grid item xs={12} md={7}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                {/* Basic Info */}
                                <Box>
                                    <Typography sx={{ color: '#12c099', fontWeight: 600, mb: 2, fontSize: '0.85rem' }}>
                                        BASIC INFORMATION
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={8}>
                                            <TextField
                                                fullWidth
                                                label="Experience Name"
                                                value={formData.sportName}
                                                onChange={(e) => handleChange('sportName', e.target.value)}
                                                sx={{ '& .MuiOutlinedInput-root': { color: '#fff', bgcolor: '#252525' }, '& .MuiInputLabel-root': { color: '#888' }, '& fieldset': { borderColor: '#444' } }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <FormControl fullWidth>
                                                <InputLabel sx={{ color: '#888' }}>Category</InputLabel>
                                                <Select
                                                    value={formData.category}
                                                    label="Category"
                                                    onChange={(e) => handleChange('category', e.target.value)}
                                                    sx={{ color: '#fff', bgcolor: '#252525', '& fieldset': { borderColor: '#444' } }}
                                                >
                                                    {CATEGORIES.slice(1).map(cat => (
                                                        <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </Box>

                                {/* Instructor */}
                                <Box>
                                    <Typography sx={{ color: '#12c099', fontWeight: 600, mb: 2, fontSize: '0.85rem' }}>
                                        INSTRUCTOR
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <TextField
                                                fullWidth
                                                label="Hourly Rate"
                                                type="number"
                                                value={formData.instructorHourlyRate}
                                                onChange={(e) => handleChange('instructorHourlyRate', Number(e.target.value))}
                                                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                                sx={{ '& .MuiOutlinedInput-root': { color: '#fff', bgcolor: '#252525' }, '& .MuiInputLabel-root': { color: '#888' }, '& fieldset': { borderColor: '#444' } }}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                fullWidth
                                                label="Duration"
                                                type="number"
                                                inputProps={{ step: 0.25 }}
                                                value={formData.durationHours}
                                                onChange={(e) => handleChange('durationHours', Number(e.target.value))}
                                                InputProps={{ endAdornment: <InputAdornment position="end">hours</InputAdornment> }}
                                                sx={{ '& .MuiOutlinedInput-root': { color: '#fff', bgcolor: '#252525' }, '& .MuiInputLabel-root': { color: '#888' }, '& fieldset': { borderColor: '#444' } }}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>

                                {/* Equipment */}
                                <Box>
                                    <Typography sx={{ color: '#12c099', fontWeight: 600, mb: 2, fontSize: '0.85rem' }}>
                                        EQUIPMENT
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <TextField
                                                fullWidth
                                                label="Per Person Cost"
                                                type="number"
                                                inputProps={{ step: 0.25 }}
                                                value={formData.equipmentPerPersonTotal}
                                                onChange={(e) => handleChange('equipmentPerPersonTotal', Number(e.target.value))}
                                                helperText="Mats, props, consumables"
                                                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                                sx={{ '& .MuiOutlinedInput-root': { color: '#fff', bgcolor: '#252525' }, '& .MuiInputLabel-root': { color: '#888' }, '& fieldset': { borderColor: '#444' }, '& .MuiFormHelperText-root': { color: '#555' } }}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                fullWidth
                                                label="Fixed Cost"
                                                type="number"
                                                value={formData.equipmentFixedTotal}
                                                onChange={(e) => handleChange('equipmentFixedTotal', Number(e.target.value))}
                                                helperText="Sound system, specialty gear"
                                                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                                sx={{ '& .MuiOutlinedInput-root': { color: '#fff', bgcolor: '#252525' }, '& .MuiInputLabel-root': { color: '#888' }, '& fieldset': { borderColor: '#444' }, '& .MuiFormHelperText-root': { color: '#555' } }}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>

                                {/* Operations */}
                                <Box>
                                    <Typography sx={{ color: '#12c099', fontWeight: 600, mb: 2, fontSize: '0.85rem' }}>
                                        OPERATIONS
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <TextField
                                                fullWidth
                                                label="Logistics Cost"
                                                type="number"
                                                value={formData.logisticsCost}
                                                onChange={(e) => handleChange('logisticsCost', Number(e.target.value))}
                                                helperText="Delivery & pickup"
                                                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                                sx={{ '& .MuiOutlinedInput-root': { color: '#fff', bgcolor: '#252525' }, '& .MuiInputLabel-root': { color: '#888' }, '& fieldset': { borderColor: '#444' }, '& .MuiFormHelperText-root': { color: '#555' } }}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <FormControl fullWidth>
                                                <InputLabel sx={{ color: '#888' }}>Risk Level</InputLabel>
                                                <Select
                                                    value={formData.riskLevel}
                                                    label="Risk Level"
                                                    onChange={(e) => handleChange('riskLevel', e.target.value)}
                                                    sx={{ color: '#fff', bgcolor: '#252525', '& fieldset': { borderColor: '#444' } }}
                                                >
                                                    {RISK_LEVELS.map(risk => (
                                                        <MenuItem key={risk.value} value={risk.value}>
                                                            {risk.label} (${risk.insurance} insurance)
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </Box>

                                {/* Venue */}
                                <Box>
                                    <Typography sx={{ color: '#12c099', fontWeight: 600, mb: 2, fontSize: '0.85rem' }}>
                                        VENUE
                                    </Typography>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={6}>
                                            <TextField
                                                fullWidth
                                                label="Off-Site Venue Rate"
                                                type="number"
                                                value={formData.offsiteVenueHourlyRate}
                                                onChange={(e) => handleChange('offsiteVenueHourlyRate', Number(e.target.value))}
                                                InputProps={{
                                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                                    endAdornment: <InputAdornment position="end">/hr</InputAdornment>
                                                }}
                                                sx={{ '& .MuiOutlinedInput-root': { color: '#fff', bgcolor: '#252525' }, '& .MuiInputLabel-root': { color: '#888' }, '& fieldset': { borderColor: '#444' } }}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={formData.requiresVenue}
                                                        onChange={(e) => handleChange('requiresVenue', e.target.checked)}
                                                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#12c099' } }}
                                                    />
                                                }
                                                label="Always requires venue"
                                                sx={{ color: '#888' }}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>

                                {/* Settings */}
                                <Box>
                                    <Typography sx={{ color: '#12c099', fontWeight: 600, mb: 2, fontSize: '0.85rem' }}>
                                        SETTINGS
                                    </Typography>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={6}>
                                            <TextField
                                                fullWidth
                                                label="Base Group Size"
                                                type="number"
                                                value={formData.baseGroupSize}
                                                onChange={(e) => handleChange('baseGroupSize', Number(e.target.value))}
                                                InputProps={{ endAdornment: <InputAdornment position="end">people</InputAdornment> }}
                                                sx={{ '& .MuiOutlinedInput-root': { color: '#fff', bgcolor: '#252525' }, '& .MuiInputLabel-root': { color: '#888' }, '& fieldset': { borderColor: '#444' } }}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={formData.isActive}
                                                        onChange={(e) => handleChange('isActive', e.target.checked)}
                                                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#12c099' } }}
                                                    />
                                                }
                                                label="Active"
                                                sx={{ color: '#888' }}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Box>
                        </Grid>

                        {/* Right Column - Preview */}
                        <Grid item xs={12} md={5}>
                            <Box sx={{ bgcolor: '#141414', borderRadius: 2, p: 3, border: '1px solid #333', position: 'sticky', top: 20 }}>
                                <Typography sx={{ color: '#888', fontSize: '0.75rem', letterSpacing: 1, mb: 2 }}>
                                    PRICING PREVIEW
                                </Typography>

                                <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, mb: 1 }}>
                                    {formData.sportName || 'Experience Name'}
                                </Typography>

                                <Chip
                                    label={formData.category}
                                    size="small"
                                    sx={{
                                        bgcolor: `${getCategoryColor(formData.category)}20`,
                                        color: getCategoryColor(formData.category),
                                        textTransform: 'capitalize',
                                        mb: 3
                                    }}
                                />

                                {/* Cost Breakdown */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography sx={{ color: '#666', fontSize: '0.75rem', mb: 1 }}>COST BREAKDOWN</Typography>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography sx={{ color: '#888', fontSize: '0.85rem' }}>Instructor</Typography>
                                        <Typography sx={{ color: '#fff', fontSize: '0.85rem' }}>${preview.instructorCost}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography sx={{ color: '#888', fontSize: '0.85rem' }}>Equipment</Typography>
                                        <Typography sx={{ color: '#fff', fontSize: '0.85rem' }}>${preview.equipmentCost}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography sx={{ color: '#888', fontSize: '0.85rem' }}>Logistics</Typography>
                                        <Typography sx={{ color: '#fff', fontSize: '0.85rem' }}>${formData.logisticsCost}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography sx={{ color: '#888', fontSize: '0.85rem' }}>Insurance</Typography>
                                        <Typography sx={{ color: '#fff', fontSize: '0.85rem' }}>${formData.insuranceCost}</Typography>
                                    </Box>
                                    <Divider sx={{ borderColor: '#333', my: 1 }} />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography sx={{ color: '#fff', fontWeight: 600 }}>Delivery Cost</Typography>
                                        <Typography sx={{ color: '#fff', fontWeight: 600 }}>${preview.deliveryCost}</Typography>
                                    </Box>
                                </Box>

                                {/* Pricing Cards */}
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={6}>
                                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#1e1e1e', borderRadius: 2, border: '1px solid #333' }}>
                                            <Typography sx={{ color: '#888', fontSize: '0.7rem', mb: 0.5 }}>ON-SITE</Typography>
                                            <Typography sx={{ color: '#12c099', fontSize: '2rem', fontWeight: 700 }}>
                                                {preview.creditsOnsite}
                                            </Typography>
                                            <Typography sx={{ color: '#666', fontSize: '0.8rem' }}>credits</Typography>
                                            <Typography sx={{ color: '#888', fontSize: '0.9rem', mt: 1 }}>
                                                ${preview.priceOnsite}
                                            </Typography>
                                            <Chip
                                                label={`${preview.marginOnsite}% margin`}
                                                size="small"
                                                sx={{ mt: 1, bgcolor: 'rgba(18,192,153,0.2)', color: '#12c099', fontSize: '0.7rem' }}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#1e1e1e', borderRadius: 2, border: '1px solid #333' }}>
                                            <Typography sx={{ color: '#888', fontSize: '0.7rem', mb: 0.5 }}>OFF-SITE</Typography>
                                            <Typography sx={{ color: '#C63D57', fontSize: '2rem', fontWeight: 700 }}>
                                                {preview.creditsOffsite}
                                            </Typography>
                                            <Typography sx={{ color: '#666', fontSize: '0.8rem' }}>credits</Typography>
                                            <Typography sx={{ color: '#888', fontSize: '0.9rem', mt: 1 }}>
                                                ${preview.priceOffsite}
                                            </Typography>
                                            <Chip
                                                label={`${preview.marginOffsite}% margin`}
                                                size="small"
                                                sx={{ mt: 1, bgcolor: 'rgba(198,61,87,0.2)', color: '#C63D57', fontSize: '0.7rem' }}
                                            />
                                        </Box>
                                    </Grid>
                                </Grid>

                                {/* Info */}
                                <Box sx={{ p: 2, bgcolor: '#0a0a0a', borderRadius: 1 }}>
                                    <Typography sx={{ color: '#666', fontSize: '0.75rem' }}>
                                        {formData.baseGroupSize} attendees • {formData.durationHours}hr • {formData.riskLevel} risk
                                    </Typography>
                                    {formData.requiresVenue && (
                                        <Typography sx={{ color: '#ff9800', fontSize: '0.75rem', mt: 0.5 }}>
                                            ⚠️ Always requires venue rental
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ borderTop: '1px solid #333', p: 2 }}>
                    <Button onClick={closeDialog} sx={{ color: '#888' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={saving || !formData.sportName.trim()}
                        sx={{ bgcolor: '#12c099', '&:hover': { bgcolor: '#0fa17d' }, minWidth: 120 }}
                    >
                        {saving ? <CircularProgress size={20} /> : (selectedCost ? 'Update' : 'Create')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                PaperProps={{ sx: { bgcolor: '#1e1e1e', color: '#fff' } }}
            >
                <DialogTitle>Delete Experience?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete <strong>"{selectedCost?.sportName}"</strong>?
                    </Typography>
                    <Typography sx={{ color: '#888', mt: 1, fontSize: '0.9rem' }}>
                        This will deactivate the experience. Use hard delete for permanent removal.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: '#888' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleDelete}
                        disabled={saving}
                        sx={{ bgcolor: '#C63D57', '&:hover': { bgcolor: '#a82e47' } }}
                    >
                        {saving ? <CircularProgress size={20} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Success Snackbar */}
            <Snackbar
                open={!!success}
                autoHideDuration={3000}
                onClose={() => setSuccess(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity="success" onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ExperiencePricing;
